import type { Schedule } from './events.js';
import type { Habit } from './state.js';
import { dayIndex } from './time.js';
import type { StudyDate } from './time.js';

/** 1970-01-01 was a Thursday; 0 = Sunday, matching the Bangladeshi week. */
export function dayOfWeek(date: StudyDate): number {
  return (((dayIndex(date) + 4) % 7) + 7) % 7;
}

/**
 * Was this habit meant to be done on this day?
 *
 * The grid needs three cell states, not two — done, missed, and *not
 * scheduled*. Greying out Friday for a weekday habit is the difference
 * between "you failed" and "not today", and getting that wrong makes an
 * honest student feel like a bad one.
 */
export function isScheduledOn(habit: Habit, date: StudyDate): boolean {
  if (habit.archived) return false;
  if (habit.createdOn > date) return false;
  return matchesSchedule(habit.schedule, date);
}

export function matchesSchedule(schedule: Schedule, date: StudyDate): boolean {
  switch (schedule.kind) {
    case 'daily':
      return true;
    case 'weekly':
      return schedule.days.includes(dayOfWeek(date));
    case 'timesPerWeek':
      // No fixed days — any day counts, so the grid never shows it as missed.
      return true;
  }
}

/**
 * How much this habit contributes to a day's "expected" count.
 *
 * `timesPerWeek` is fractional on purpose: a 3×/week habit expects 3/7 of a
 * completion each day, so a completion-rate average over a window comes out
 * right without tracking which specific days were chosen.
 */
export function expectedWeight(habit: Habit, date: StudyDate): number {
  if (habit.archived || habit.createdOn > date) return 0;
  if (habit.schedule.kind === 'daily') return 1;
  if (habit.schedule.kind === 'weekly') return habit.schedule.days.includes(dayOfWeek(date)) ? 1 : 0;
  return habit.schedule.count / 7;
}
